<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import DOMPurify from "dompurify";
import { useAuthStore } from "@/stores/auth";
import Swal from "sweetalert2";
import SocialLoginButtons from "@/components/auth/SocialLoginButtons.vue";
import LoginForm from "@/components/auth/LoginForm.vue";
import AuthLayout from "@/components/layouts/AuthLayout.vue";

const router = useRouter();
const authStore = useAuthStore();
const isLoading = ref(false);
const checkingAuth = ref(true);

onMounted(async () => {
  checkingAuth.value = true;
  try {
    // Usar el método del store para verificar y redirigir si ya está autenticado
    await authStore.redirectIfAuthenticated("/dashboard");
  } catch (error) {
    console.error("Error verificando autenticación:", error);
  } finally {
    checkingAuth.value = false;
  }
});

const sanitizeInput = (input) => {
  if (typeof input !== "string") return input;
  return DOMPurify.sanitize(input.trim());
};

const onSubmit = async (formValues) => {
  try {
    isLoading.value = true;

    Swal.fire({
      title: "Iniciando sesión",
      html: "Por favor, espera...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
      background: "rgba(255, 255, 255, 0.95)",
      backdrop: "rgba(74, 92, 40, 0.4)",
      customClass: {
        title: "text-[#4a5c28]",
        popup: "rounded-xl border border-[#99b642]/30",
      },
    });

    const sanitizedValues = {
      email: sanitizeInput(formValues.email.toLowerCase()),
      password: formValues.password,
    };

    // Usar el método login del store
    await authStore.login(sanitizedValues);

    Swal.close();

    Swal.fire({
      icon: "success",
      title: "¡Bienvenido de nuevo!",
      text: "Inicio de sesión exitoso",
      timer: 2000,
      showConfirmButton: false,
      background: "rgba(255, 255, 255, 0.95)",
      backdrop: "rgba(74, 92, 40, 0.7)",
      customClass: {
        title: "text-[#4a5c28]",
        popup: "rounded-xl border border-[#99b642]/30",
      },
    });

    setTimeout(() => {
      router.push("/dashboard");
    }, 2000);
  } catch (error) {
    Swal.close();
    let errorMessage = "Error al iniciar sesión";
    if (error.response && error.response.data && error.response.data.message) {
      errorMessage = error.response.data.message;
    } else if (authStore.error) {
      errorMessage = authStore.error;
    }
    Swal.fire({
      icon: "error",
      title: errorMessage,
      confirmButtonText: "Intentar nuevamente",
      confirmButtonColor: "#99b642",
      background: "rgba(255, 255, 255, 0.95)",
      customClass: {
        confirmButton: "bg-[#99b642] text-white px-4 py-2 rounded-lg",
        title: "text-red-600",
        popup: "rounded-xl border border-red-100",
      },
    });
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <!-- Mostrar un loader mientras se verifica la autenticación -->
  <div
    v-if="checkingAuth"
    class="min-h-screen flex justify-center items-center bg-gray-100"
  >
    <div class="loader-pulse"></div>
  </div>

  <!-- Mostrar el formulario de login solo si no está autenticado -->
  <AuthLayout v-else>
    <template #title>
      <h1 class="text-4xl font-extrabold text-white mb-2 drop-shadow-lg">
        Iniciar sesión
      </h1>
    </template>

    <LoginForm @submit="onSubmit">
      <template #button-content>
        <span v-if="isLoading" class="flex items-center justify-center">
          <div
            class="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
          ></div>
          Iniciando sesión...
        </span>
        <span v-else class="flex items-center justify-center">
          <i class="ri-login-box-line mr-2 text-xl"></i>
          Iniciar sesión
        </span>
      </template>
    </LoginForm>

    <div class="max-w-md mx-auto space-y-6">
      <SocialLoginButtons
        mode="login"
        @google-auth-success="handleAuthSuccess"
      />
    </div>
    <template #footer-links>
      <p class="text-center text-white mt-6">
        ¿No tienes cuenta?
        <router-link
          to="/register"
          class="font-semibold underline hover:text-[#d4e4a9] transition-colors ml-1"
        >
          Regístrate aquí
        </router-link>
      </p>
    </template>
  </AuthLayout>
</template>

<style scoped>
.loader-pulse {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #99b642;
  box-shadow: 0 0 0 0 rgba(153, 182, 66, 1);
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(153, 182, 66, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 15px rgba(153, 182, 66, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(153, 182, 66, 0);
  }
}
</style>
